import React, { useId } from 'react'
import { ReactComponent as ArrowDownIcon } from '@assets/images/Icons/Pagination_arrow_down.svg'
import { ReactComponent as ChevronLeftIcon } from '@assets/images/Icons/Pagination_chevron_left.svg'
import { ReactComponent as ChevronRightIcon } from '@assets/images/Icons/Pagination_chevron_right.svg'
import { ReactComponent as ChevronsLeftIcon } from '@assets/images/Icons/Pagination_chevrons_left.svg'
import { ReactComponent as ChevronsRightIcon } from '@assets/images/Icons/Pagination_chevrons_right.svg'
import type { PaginationProps } from './Pagination.types'
import { PAGINATION_PREVIEW_CONFIG } from './Pagination.types'
import {
  buildPaginationItems,
  DEFAULT_PAGINATION_PAGE_SIZE_OPTIONS,
  formatPaginationPageSizeLabel,
} from './Pagination.utils'
import './Pagination.css'

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  pageSize,
  pageSizeOptions = [...DEFAULT_PAGINATION_PAGE_SIZE_OPTIONS],
  onPageChange,
  onPageSizeChange,
  previewState,
  darkMode = false,
  disabled = false,
  className = '',
}) => {
  const selectId = useId()
  const isPreview = previewState != null
  const previewConfig = previewState
    ? PAGINATION_PREVIEW_CONFIG[previewState]
    : null

  const resolvedCurrentPage = previewConfig?.currentPage ?? currentPage
  const resolvedTotalPages = previewConfig?.totalPages ?? totalPages
  const resolvedPageSize = previewConfig?.pageSize ?? pageSize
  const safeTotalPages = Math.max(resolvedTotalPages, 1)
  // Preview locks interaction via CSS; keep visual disabled only for
  // boundary nav / the real `disabled` prop so the matrix matches.
  const blockInteraction = disabled || isPreview
  const atFirstPage = resolvedCurrentPage <= 1
  const atLastPage = resolvedCurrentPage >= safeTotalPages
  const pageItems = buildPaginationItems(resolvedCurrentPage, safeTotalPages)
  const resolvedPageSizeOptions = pageSizeOptions.includes(resolvedPageSize)
    ? pageSizeOptions
    : [...pageSizeOptions, resolvedPageSize].sort((a, b) => a - b)

  const handlePageChange = (page: number): void => {
    if (blockInteraction || !onPageChange) {
      return
    }

    onPageChange(page)
  }

  const handlePageSizeChange = (nextPageSize: number): void => {
    if (blockInteraction || !onPageSizeChange) {
      return
    }

    onPageSizeChange(nextPageSize)
  }

  return (
    <div
      className={`ds-pagination${isPreview ? ' ds-pagination--preview' : ''}${
        darkMode ? ' ds-pagination--dark' : ''
      } ${className}`.trim()}
      data-preview-state={previewState}
    >
      <div className="ds-pagination__page-size">
        <label className="ds-pagination__page-size-label" htmlFor={selectId}>
          Results per page
        </label>
        <div className="ds-pagination__page-size-control">
          <select
            id={selectId}
            className="ds-pagination__page-size-select"
            value={resolvedPageSize}
            onChange={(event) =>
              handlePageSizeChange(Number(event.target.value))
            }
            disabled={disabled || (!isPreview && !onPageSizeChange)}
          >
            {resolvedPageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {formatPaginationPageSizeLabel(option)}
              </option>
            ))}
          </select>
          <ArrowDownIcon
            className="ds-pagination__dropdown-icon"
            aria-hidden="true"
          />
        </div>
      </div>

      <div
        className="ds-pagination__controls"
        role="navigation"
        aria-label="Pagination"
      >
        <button
          type="button"
          className="ds-pagination__nav-btn"
          aria-label="First page"
          disabled={disabled || atFirstPage}
          onClick={() => handlePageChange(1)}
          tabIndex={isPreview ? -1 : 0}
        >
          <ChevronsLeftIcon
            className="ds-pagination__nav-icon"
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          className="ds-pagination__nav-btn"
          aria-label="Previous page"
          disabled={disabled || atFirstPage}
          onClick={() => handlePageChange(resolvedCurrentPage - 1)}
          tabIndex={isPreview ? -1 : 0}
        >
          <ChevronLeftIcon
            className="ds-pagination__nav-icon"
            aria-hidden="true"
          />
        </button>

        {pageItems.map((item) =>
          item.type === 'ellipsis' ? (
            <span
              key={item.key}
              className="ds-pagination__ellipsis"
              aria-hidden="true"
            >
              ...
            </span>
          ) : (
            <button
              key={item.value}
              type="button"
              className={`ds-pagination__page-btn${
                item.value === resolvedCurrentPage
                  ? ' ds-pagination__page-btn--active'
                  : ''
              }`}
              aria-current={
                item.value === resolvedCurrentPage ? 'page' : undefined
              }
              disabled={disabled}
              onClick={() => handlePageChange(item.value)}
              tabIndex={isPreview ? -1 : 0}
            >
              {item.value}
            </button>
          ),
        )}

        <button
          type="button"
          className="ds-pagination__nav-btn"
          aria-label="Next page"
          disabled={disabled || atLastPage}
          onClick={() => handlePageChange(resolvedCurrentPage + 1)}
          tabIndex={isPreview ? -1 : 0}
        >
          <ChevronRightIcon
            className="ds-pagination__nav-icon"
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          className="ds-pagination__nav-btn"
          aria-label="Last page"
          disabled={disabled || atLastPage}
          onClick={() => handlePageChange(safeTotalPages)}
          tabIndex={isPreview ? -1 : 0}
        >
          <ChevronsRightIcon
            className="ds-pagination__nav-icon"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  )
}

export default Pagination
